/**
 * S3 Signature Service
 * 
 * Handles uploading, retrieving, listing, and deleting user signatures from S3
 * Supports up to 5 signatures per user (slots 1-5)
 * Slot 1 is always the default signature
 * 
 * FIXES APPLIED:
 * - Proper timestamp handling for cache expiry
 * - Better error handling and retry logic
 * - Fixed presigned URL expiry detection
 * - Added network status checking
 * - Improved logging for debugging
 */

const SIGNATURE_API_ENDPOINT = process.env.REACT_APP_SIGNATURE_API_URL || 
  'https://r3vefztcpc.execute-api.ap-southeast-2.amazonaws.com/default/store-efs-report-photos-in-s3';

const MAX_SIGNATURES = 5;
const MAX_SIGNATURE_SIZE_MB = 5;
const PRESIGNED_URL_BUFFER = 3600; // Refresh URLs 1 hour before expiry (in seconds)
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Cache for presigned URLs
const urlCache = new Map();

// In-flight request tracking
const inFlightRequests = new Map();

/**
 * Helper: Retry with exponential backoff
 */
const retryWithBackoff = async (fn, retries = MAX_RETRIES, delay = RETRY_DELAY) => {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;
    
    // Don't retry on 4xx errors except 429 (rate limit)
    if (error.statusCode) {
      if (error.statusCode >= 400 && error.statusCode < 500 && error.statusCode !== 429) {
        throw error;
      }
    }
    
    console.warn(`[SignatureService] Retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES}) - ${error.message}`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryWithBackoff(fn, retries - 1, delay * 2);
  }
};

/**
 * Helper: Check if presigned URL is expired or near expiration
 * FIXED: Proper timestamp comparison
 */
const isUrlExpiredOrNearExpiry = (urlData) => {
  if (!urlData || !urlData.generatedAt || !urlData.expiresIn) {
    console.warn('[SignatureService] Missing cache data, treating as expired');
    return true;
  }
  
  try {
    const generatedTime = new Date(urlData.generatedAt).getTime();
    const now = Date.now();
    const urlAge = (now - generatedTime) / 1000; // Age in seconds
    const remainingTime = urlData.expiresIn - urlAge;
    
    const isExpired = remainingTime <= PRESIGNED_URL_BUFFER;
    
    if (isExpired) {
      console.log(`[SignatureService] URL near expiry: ${Math.floor(remainingTime / 60)} minutes remaining`);
    }
    
    return isExpired;
  } catch (error) {
    console.error('[SignatureService] Error checking expiry:', error);
    return true; // Treat as expired if error
  }
};

/**
 * Helper: Validate signature data URL format and size
 */
const validateSignatureData = (signatureDataUrl) => {
  if (!signatureDataUrl || typeof signatureDataUrl !== 'string') {
    throw new Error('Invalid signature data: must be a string');
  }
  
  if (!signatureDataUrl.startsWith('data:image/')) {
    throw new Error('Invalid signature data: must be a data URL starting with data:image/');
  }
  
  // Estimate size (base64 is ~33% larger than binary)
  const base64Length = signatureDataUrl.split(',')[1]?.length || 0;
  const estimatedSizeMB = (base64Length * 0.75) / (1024 * 1024);
  
  if (estimatedSizeMB > MAX_SIGNATURE_SIZE_MB) {
    throw new Error(`Signature too large: ${estimatedSizeMB.toFixed(2)}MB (max: ${MAX_SIGNATURE_SIZE_MB}MB)`);
  }
  
  return true;
};

/**
 * Helper: Create cache key
 */
const getCacheKey = (username, slotNumber) => `${username}_slot${slotNumber}`;

/**
 * Helper: Get cached URL if valid
 */
const getCachedUrl = (username, slotNumber) => {
  const cacheKey = getCacheKey(username, slotNumber);
  const cached = urlCache.get(cacheKey);
  
  if (cached && !isUrlExpiredOrNearExpiry(cached)) {
    const generatedTime = new Date(cached.generatedAt).getTime();
    const now = Date.now();
    const hoursRemaining = Math.floor((cached.expiresIn - (now - generatedTime) / 1000) / 3600);
    
    console.log(`[SignatureService] Using cached URL for ${cacheKey} (${hoursRemaining}h remaining)`);
    return cached.url;
  }
  
  // Remove expired cache entry
  if (cached) {
    console.log(`[SignatureService] Removing expired cache for ${cacheKey}`);
    urlCache.delete(cacheKey);
  }
  
  return null;
};

/**
 * Helper: Cache presigned URL
 * FIXED: Store generatedAt timestamp from server
 */
const cacheUrl = (username, slotNumber, url, expiresIn, generatedAt) => {
  const cacheKey = getCacheKey(username, slotNumber);
  const cacheData = {
    url,
    expiresIn,
    generatedAt: generatedAt || new Date().toISOString()
  };
  
  urlCache.set(cacheKey, cacheData);
  console.log(`[SignatureService] Cached URL for ${cacheKey}, expires in ${Math.floor(expiresIn / 3600)}h`);
};

/**
 * Helper: Create detailed error from API response
 */
const createDetailedError = async (response, defaultMessage) => {
  let data;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : { error: defaultMessage };
  } catch (e) {
    data = { error: defaultMessage };
  }
  
  const error = new Error(data.error || data.message || defaultMessage);
  error.statusCode = response.status;
  error.code = data.code;
  error.details = data.details;
  error.bucket = data.bucket;
  error.region = data.region;
  error.troubleshooting = data.troubleshooting;
  
  return error;
};

/**
 * Helper: Format error message for user display
 */
const formatErrorMessage = (error) => {
  let message = error.message || 'Unknown error occurred';
  
  if (error.code === 'NoSuchBucket') {
    message += '\n\nTroubleshooting: The S3 bucket may not exist or the bucket name is incorrect.';
    if (error.bucket) {
      message += `\nBucket: ${error.bucket}`;
    }
  } else if (error.code === 'AccessDenied') {
    message += '\n\nTroubleshooting: The Lambda function lacks S3 permissions. Contact your administrator.';
  } else if (error.statusCode === 404) {
    // 404 is often expected (no signature exists)
  } else if (!navigator.onLine) {
    message = 'Network error: You appear to be offline. Please check your internet connection.';
  } else if (error.statusCode >= 500) {
    message += '\n\nServer error: Please try again in a few moments. If the problem persists, contact support.';
  }
  
  if (error.troubleshooting) {
    message += `\n\n${error.troubleshooting}`;
  }
  
  return message;
};

/**
 * Helper: Test if URL is accessible
 */
const testUrlAccess = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD', mode: 'cors' });
    return response.ok;
  } catch (error) {
    console.error('[SignatureService] URL access test failed:', error);
    return false;
  }
};

/**
 * List all signatures for a user
 * WITH AUTO-REFRESH: Automatically refreshes expired URLs
 * 
 * @param {string} username - Cognito username
 * @param {boolean} refreshExpired - Auto-refresh expired URLs (default: true)
 * @returns {Promise<{success: boolean, signatures: Array, count: number}>}
 */
export const listSignatures = async (username, refreshExpired = true) => {
  try {
    if (!username) {
      throw new Error('Username is required');
    }

    console.log(`[SignatureService] Listing signatures for ${username}`);

    const result = await retryWithBackoff(async () => {
      const response = await fetch(SIGNATURE_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          action: 'list'
        })
      });

      if (!response.ok) {
        throw await createDetailedError(response, 'Failed to list signatures');
      }

      const data = await response.json();
      if (!data.success) {
        const error = new Error(data.error || data.message || 'Failed to list signatures');
        error.statusCode = response.status;
        throw error;
      }

      return data;
    });

    console.log(`[SignatureService] Found ${result.count} signatures`);

    // Cache all presigned URLs from the list
    if (result.success && result.signatures) {
      for (const sig of result.signatures) {
        if (sig.url && sig.expiresIn) {
          cacheUrl(username, sig.slotNumber, sig.url, sig.expiresIn, sig.generatedAt);
        }
      }

      // Check for expired URLs and refresh if needed
      if (refreshExpired) {
        const needsRefresh = result.signatures.filter(sig => {
          const cached = urlCache.get(getCacheKey(username, sig.slotNumber));
          return cached && isUrlExpiredOrNearExpiry(cached);
        });

        if (needsRefresh.length > 0) {
          console.log(`[SignatureService] Refreshing ${needsRefresh.length} expired URLs...`);
          
          const refreshPromises = needsRefresh.map(sig => 
            retrieveSignatureFromS3(username, sig.slotNumber, true)
              .then(fresh => {
                if (fresh.success) {
                  const index = result.signatures.findIndex(s => s.slotNumber === sig.slotNumber);
                  if (index !== -1) {
                    result.signatures[index].url = fresh.s3Url;
                    result.signatures[index].expiresIn = fresh.expiresIn;
                    result.signatures[index].generatedAt = fresh.generatedAt;
                  }
                }
              })
              .catch(err => console.error(`[SignatureService] Failed to refresh slot ${sig.slotNumber}:`, err))
          );
          
          await Promise.all(refreshPromises);
        }
      }
    }

    return result;
  } catch (error) {
    console.error('[SignatureService] Error listing signatures:', {
      username,
      errorMessage: error.message,
      statusCode: error.statusCode,
      code: error.code
    });
    error.userMessage = formatErrorMessage(error);
    throw error;
  }
};

/**
 * Upload signature to S3 in a specific slot
 * WITH VALIDATION: Checks size and format before upload
 * 
 * @param {string} username - Cognito username
 * @param {string} signatureDataUrl - Base64 data URL of signature image
 * @param {number} slotNumber - Slot number (1-5), defaults to 1
 * @returns {Promise<{success: boolean, s3Url: string, slotNumber: number, message: string}>}
 */
export const uploadSignatureToS3 = async (username, signatureDataUrl, slotNumber = 1) => {
  try {
    if (!username) {
      throw new Error('Username is required');
    }

    if (!signatureDataUrl) {
      throw new Error('Signature data is required');
    }

    // Validate signature data
    validateSignatureData(signatureDataUrl);

    if (slotNumber < 1 || slotNumber > MAX_SIGNATURES) {
      throw new Error(`Slot number must be between 1 and ${MAX_SIGNATURES}`);
    }

    console.log(`[SignatureService] Uploading signature for ${username} to slot ${slotNumber}`);

    // Clear cache for this slot
    const cacheKey = getCacheKey(username, slotNumber);
    urlCache.delete(cacheKey);

    const result = await retryWithBackoff(async () => {
      const response = await fetch(SIGNATURE_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          action: 'upload',
          signatureData: signatureDataUrl,
          slotNumber
        })
      });

      if (!response.ok) {
        throw await createDetailedError(response, 'Failed to upload signature');
      }

      const data = await response.json();
      if (!data.success) {
        const error = new Error(data.error || data.message || 'Failed to upload signature');
        error.statusCode = response.status;
        throw error;
      }

      // Cache the new presigned URL
      if (data.success && data.s3Url && data.expiresIn) {
        cacheUrl(username, slotNumber, data.s3Url, data.expiresIn, data.generatedAt);
      }

      return data;
    });

    console.log(`[SignatureService] Signature uploaded successfully to slot ${slotNumber}`);
    return result;
  } catch (error) {
    console.error('[SignatureService] Error uploading signature:', {
      username,
      slotNumber,
      errorMessage: error.message,
      statusCode: error.statusCode,
      code: error.code
    });
    error.userMessage = formatErrorMessage(error);
    throw error;
  }
};

/**
 * Retrieve signature from a specific slot
 * WITH AUTO-REFRESH: Automatically fetches fresh presigned URL if expired
 * 
 * @param {string} username - Cognito username
 * @param {number} slotNumber - Slot number (1-5), defaults to 1
 * @param {boolean} forceRefresh - Force fetch fresh URL even if cached
 * @returns {Promise<{success: boolean, s3Url: string, signatureData: string, slotNumber: number}>}
 */
export const retrieveSignatureFromS3 = async (username, slotNumber = 1, forceRefresh = false) => {
  try {
    if (!username) {
      throw new Error('Username is required');
    }

    if (slotNumber < 1 || slotNumber > MAX_SIGNATURES) {
      throw new Error(`Slot number must be between 1 and ${MAX_SIGNATURES}`);
    }

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedUrl = getCachedUrl(username, slotNumber);
      if (cachedUrl) {
        return {
          success: true,
          s3Url: cachedUrl,
          signatureData: null,
          slotNumber,
          fromCache: true
        };
      }
    }

    console.log(`[SignatureService] Retrieving signature for ${username} from slot ${slotNumber}${forceRefresh ? ' (force refresh)' : ''}`);

    // Prevent duplicate concurrent requests
    const requestKey = `retrieve_${username}_${slotNumber}`;
    if (inFlightRequests.has(requestKey)) {
      console.log(`[SignatureService] Reusing in-flight request for ${requestKey}`);
      return await inFlightRequests.get(requestKey);
    }

    const requestPromise = retryWithBackoff(async () => {
      const response = await fetch(SIGNATURE_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          action: 'retrieve',
          slotNumber
        })
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`[SignatureService] No signature found in slot ${slotNumber}`);
          return { success: false, s3Url: null, signatureData: null, slotNumber };
        }
        throw await createDetailedError(response, 'Failed to retrieve signature');
      }

      const data = await response.json();
      if (!data.success && response.status !== 404) {
        const error = new Error(data.error || data.message || 'Failed to retrieve signature');
        error.statusCode = response.status;
        throw error;
      }

      // Cache the presigned URL
      if (data.success && data.s3Url && data.expiresIn) {
        cacheUrl(username, slotNumber, data.s3Url, data.expiresIn, data.generatedAt);
      }

      return data;
    });

    inFlightRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      console.log(`[SignatureService] Retrieved signature from slot ${slotNumber}:`, result.success ? 'success' : 'not found');
      return result;
    } finally {
      inFlightRequests.delete(requestKey);
    }
  } catch (error) {
    console.error('[SignatureService] Error retrieving signature:', {
      username,
      slotNumber,
      errorMessage: error.message,
      statusCode: error.statusCode,
      code: error.code
    });
    error.userMessage = formatErrorMessage(error);
    throw error;
  }
};

/**
 * Get signature with automatic fallback for expired URLs
 * RECOMMENDED: Use this instead of retrieveSignatureFromS3 for robust access
 * 
 * @param {string} username - Cognito username
 * @param {number} slotNumber - Slot number (1-5), defaults to 1
 * @returns {Promise<{success: boolean, s3Url: string, signatureData: string, slotNumber: number}>}
 */
export const getSignatureWithFallback = async (username, slotNumber = 1) => {
  try {
    console.log(`[SignatureService] Getting signature with fallback for ${username}, slot ${slotNumber}`);
    
    // Try to get signature (checks cache first)
    let result = await retrieveSignatureFromS3(username, slotNumber, false);
    
    if (!result.success) {
      return result;
    }

    // If we got a cached URL, test if it's still accessible
    if (result.fromCache && result.s3Url) {
      console.log('[SignatureService] Testing cached URL accessibility...');
      const isAccessible = await testUrlAccess(result.s3Url);
      
      if (!isAccessible) {
        console.warn('[SignatureService] Cached URL is inaccessible, fetching fresh URL...');
        result = await retrieveSignatureFromS3(username, slotNumber, true);
      }
    }

    return result;
  } catch (error) {
    console.error('[SignatureService] Error getting signature with fallback:', error);
    throw error;
  }
};

/**
 * Delete signature from a specific slot
 * 
 * @param {string} username - Cognito username
 * @param {number} slotNumber - Slot number (1-5), defaults to 1
 * @returns {Promise<{success: boolean, slotNumber: number, message: string}>}
 */
export const deleteSignatureFromS3 = async (username, slotNumber = 1) => {
  try {
    if (!username) {
      throw new Error('Username is required');
    }

    if (slotNumber < 1 || slotNumber > MAX_SIGNATURES) {
      throw new Error(`Slot number must be between 1 and ${MAX_SIGNATURES}`);
    }

    console.log(`[SignatureService] Deleting signature for ${username} from slot ${slotNumber}`);

    // Clear cache for this slot
    const cacheKey = getCacheKey(username, slotNumber);
    urlCache.delete(cacheKey);

    const result = await retryWithBackoff(async () => {
      const response = await fetch(SIGNATURE_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          action: 'delete',
          slotNumber
        })
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`[SignatureService] No signature found in slot ${slotNumber} to delete`);
          return { success: false, message: 'Signature not found', slotNumber };
        }
        throw await createDetailedError(response, 'Failed to delete signature');
      }

      const data = await response.json();
      if (!data.success && response.status !== 404) {
        const error = new Error(data.error || data.message || 'Failed to delete signature');
        error.statusCode = response.status;
        throw error;
      }

      return data;
    });

    console.log(`[SignatureService] Signature deleted from slot ${slotNumber}`);
    return result;
  } catch (error) {
    console.error('[SignatureService] Error deleting signature:', {
      username,
      slotNumber,
      errorMessage: error.message,
      statusCode: error.statusCode,
      code: error.code
    });
    error.userMessage = formatErrorMessage(error);
    throw error;
  }
};

/**
 * Clear all cached presigned URLs
 */
export const clearSignatureCache = () => {
  const size = urlCache.size;
  urlCache.clear();
  console.log(`[SignatureService] Cleared ${size} cached URLs`);
};

/**
 * Get cache statistics (for debugging)
 */
export const getCacheStats = () => {
  const stats = {
    totalCached: urlCache.size,
    valid: 0,
    expired: 0,
    nearExpiry: 0
  };

  urlCache.forEach((value) => {
    if (isUrlExpiredOrNearExpiry(value)) {
      try {
        const generatedTime = new Date(value.generatedAt).getTime();
        const now = Date.now();
        const urlAge = (now - generatedTime) / 1000;
        
        if (urlAge > value.expiresIn) {
          stats.expired++;
        } else {
          stats.nearExpiry++;
        }
      } catch (e) {
        stats.expired++;
      }
    } else {
      stats.valid++;
    }
  });

  console.log('[SignatureService] Cache stats:', stats);
  return stats;
};

/**
 * Check if the signature API is configured
 */
export const isSignatureAPIConfigured = () => {
  const url = process.env.REACT_APP_SIGNATURE_API_URL;
  const isConfigured = url && url !== '' && !url.includes('YOUR_API_GATEWAY_ID');
  console.log(`[SignatureService] API configured: ${isConfigured}${!isConfigured ? ' - using default/placeholder URL' : ''}`);
  return isConfigured;
};